import React, {useContext, useEffect, useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./Home.css";
import {ScreenSize} from "../../utility/ScreenSize";
import {Table} from "../../../reusables/Table";
import API, {Resolution} from "../../../logic/API";
import {
  ActiveTopicCount,
  RecentTopicDescription,
  TopicDescriptionInfo,
} from "../../../logic/APITypes";
import {KeycloakContext} from "../../utility/KeycloakProvider";
import {AppSettingsContext} from "../../utility/AppSettingsProvider";

/**
 * Landing page of the data market
 */
function Home() {
  const {keycloak} = useContext(KeycloakContext);
  const {favorites, lastVisited} = useContext(AppSettingsContext);
  const screenSize = useContext(ScreenSize);
  const intl = useIntl();

  const [recentTopicDescriptions, setRecentTopicDescriptions] = useState<
    Resolution<RecentTopicDescription[]>
  >({
    success: false,
    unresolved: true,
  });
  const [problemTopicDescriptions, setProblemTopicDescriptions] = useState<
    Resolution<TopicDescriptionInfo[]>
  >({
    success: false,
    unresolved: true,
  });
  const filteredProblemTopicDescriptions = (
    problemTopicDescriptions.payload || []
  )
    .filter(
      (description) =>
        description.health === "ACTIVE_ERROR" ||
        description.health === "PAUSED_ERROR",
    )
    .slice(0, 4);
  const [activeCount, setActiveCount] = useState<Resolution<ActiveTopicCount>>({
    success: false,
    unresolved: true,
  });
  const [animatedActiveCount, setAnimatedActiveCount] = useState(0);
  const requestedAnimationFrame = useRef<number | undefined>();

  const userName: string = keycloak.idTokenParsed
    ? keycloak.idTokenParsed["given_name"]
    : "";

  useEffect(() => {
    API.getRecentTopics(4).then((response) => {
      setRecentTopicDescriptions(response);
    });
    API.getTopicDescriptions().then((response) => {
      setProblemTopicDescriptions(response);
    });
    API.getNumberOfActiveTopics().then((response) => {
      setActiveCount(response);
      if ((response.payload?.count || 0) <= 3) {
        setAnimatedActiveCount(response.payload?.count || 0);
      } else {
        const animationDuration = Math.min(
          1000,
          (response.payload?.count || 0) * 100,
        );
        const animate = (
          currentTime: number,
          targetTime: number,
          targetCount: number,
        ) => {
          const progress = Math.min(
            (animationDuration + currentTime - targetTime) / animationDuration,
            1,
          );
          const newCount = (1 - Math.pow(1 - progress, 1.675)) * targetCount; // mimic ease-out
          setAnimatedActiveCount(newCount);
          requestedAnimationFrame.current =
            newCount == targetCount
              ? undefined
              : requestAnimationFrame((time: number) =>
                  animate(time, targetTime, targetCount),
                );
        };
        requestedAnimationFrame.current = requestAnimationFrame(
          (time: number) =>
            animate(
              time,
              time + animationDuration,
              response.payload?.count || 0,
            ),
        );
      }
    });
    return () => {
      if (requestedAnimationFrame.current)
        cancelAnimationFrame(requestedAnimationFrame.current);
    };
  }, []);

  return (
    <div className="homeContainer landingPageGrid">
      <h1>
        <FormattedMessage
          id="home.title"
          values={{
            b: (chunks) => <b>{chunks}</b>,
            userName: userName,
          }}
        />
      </h1>
      <div className="homeNewTopicsContainer homePanelContainer">
        <h2>
          <FormattedMessage id="home.newTopics" />
        </h2>
        {!recentTopicDescriptions.unresolved &&
        (!recentTopicDescriptions.success ||
          recentTopicDescriptions.payload?.length === 0) ? (
          <div className="homeBlankText">
            <FormattedMessage
              id={
                "home.newTopics." +
                (recentTopicDescriptions.success ? "blank" : "failed")
              }
            />
          </div>
        ) : (
          <Table
            id={"homeNewTopics"}
            columns={[
              {
                key: "name",
                label: <FormattedMessage id="home.newTopics.name" />,
                columnCount: {
                  "Big Desktop": 7,
                  Desktop: 7,
                  Tablet: 4,
                  Mobile: 4,
                },
              },
              {
                key: "publicationDate",
                label: <FormattedMessage id="home.newTopics.publicationDate" />,
                collapsibleMobile: true,
                columnCount: {
                  "Big Desktop": 5,
                  Desktop: 5,
                  Tablet: 4,
                },
              },
            ]}
            data={(recentTopicDescriptions.payload || []).map(
              (description) => ({
                id: description.id,
                cells: {
                  id: description.id,
                  name: {
                    content: description.name,
                    hasToolTip: true,
                    link: {
                      to: "/topics/overview/" + description.id,
                      onClick: () => {
                        localStorage.setItem(
                          "topicDescriptionCreationOriginPage",
                          "home",
                        );
                      },
                    },
                  },
                  publicationDate: {
                    sortingKey: 0,
                    content: intl.formatDate(
                      new Date(description.published_on * 1000),
                    ),
                  },
                },
              }),
            )}
            isLoading={recentTopicDescriptions.unresolved}
          />
        )}
      </div>
      <div className="homeActiveTopicsContainer homePanelContainer">
        <h2>
          <FormattedMessage id="home.activeTopics" />
        </h2>
        {!activeCount.success && !activeCount.unresolved ? (
          <div className="homeBlankText">
            <FormattedMessage id={"home.activeTopics.failed"} />
          </div>
        ) : (
          <div>
            <div className="homeActiveTopicsAsOf">
              {activeCount.unresolved
                ? intl.formatMessage({id: "home.activeTopics.unresolved"})
                : `${intl.formatMessage({
                    id: "home.activeTopics.asOf",
                  })} 
            ${intl.formatDate(new Date())}`}
            </div>
            <div>
              <div className="homeActiveTopicsNumber">
                {Math.floor(animatedActiveCount)}
              </div>
              <div>
                <FormattedMessage id="home.activeTopics.topics" />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="homeProblemTopicsContainer homePanelContainer">
        <h2>
          <FormattedMessage id="home.problemTopics" />
        </h2>
        <div>
          {!problemTopicDescriptions.unresolved &&
          (!problemTopicDescriptions.success ||
            filteredProblemTopicDescriptions.length === 0) ? (
            <div className="homeBlankText">
              <FormattedMessage
                id={
                  "home.problemTopics." +
                  (problemTopicDescriptions.success ? "blank" : "failed")
                }
              />
            </div>
          ) : (
            <Table
              id={"homeProblemTopics"}
              columns={[
                {
                  key: "name",
                  label: <FormattedMessage id="home.problemTopics.name" />,
                  columnCount: {
                    "Big Desktop": 8,
                    Desktop: 8,
                    Tablet: 4,
                    Mobile: 4,
                  },
                },
                {
                  key: "health",
                  label: <FormattedMessage id="home.problemTopics.health" />,
                  collapsibleMobile: true,
                  columnCount: {
                    "Big Desktop": 4,
                    Desktop: 4,
                    Tablet: 4,
                  },
                },
              ]}
              data={filteredProblemTopicDescriptions.map((description) => ({
                id: description.id,
                cells: {
                  id: description.id,
                  name: {
                    content: description.name,
                    hasToolTip: true,
                    link: {
                      to: "/topics/overview/" + description.id,
                      onClick: () => {
                        localStorage.setItem(
                          "topicDescriptionCreationOriginPage",
                          "home",
                        );
                      },
                    },
                  },
                  health: {
                    content: intl.formatMessage({
                      id: "home.problemTopics.health." + description.health,
                    }),
                  },
                },
              }))}
              isLoading={recentTopicDescriptions.unresolved}
            />
          )}
        </div>
      </div>
      <div className="homeActivityFavoriteContainer">
        <h2 id="homeFavorites">
          <FormattedMessage id="home.favorites" />
        </h2>
        {favorites
          .slice(
            0,
            screenSize.category === "Mobile" || screenSize.category === "Tablet"
              ? 4
              : -1,
          )
          .map((element, index) => (
            <HomeEntry key={index} {...element} />
          ))}
      </div>
      <div className="homeActivityLastVisitedContainer">
        <h2 id="homeLastVisited">
          <FormattedMessage id="home.lastVisited" />
        </h2>
        {lastVisited.map((element, index) => (
          <HomeEntry key={index} {...element} />
        ))}
      </div>
    </div>
  );
}

export {Home};

type HomeEntryProps = {
  title: string;
  subtitle: string;
  image: string;
  link?: string;
};
function HomeEntry(props: HomeEntryProps) {
  const linkProps = props.link
    ? {href: props.link, target: "_blank", rel: "noreferrer"}
    : {href: "/datamarket/topics/overview"};
  return (
    <a className="homeEntryContainer" {...linkProps}>
      <div className="homeEntryImageContainer">
        <img src={props.image} alt="" />
      </div>
      <div className="homeEntryTitleContainer">
        <FormattedMessage id={props.title} />
      </div>
      <div className="homeEntrySubtitleContainer">
        <FormattedMessage id={props.subtitle} />
      </div>
    </a>
  );
}
